import React, { useEffect, useRef } from 'react';
import { Skeleton } from 'primereact/skeleton';
import '../../../global.styles.scss';
import '../HeroSection/HeroSection.styles.scss';
import '../ContractExpirationsSection/ContractExpirationsSection.styles.scss';
import '../SearchSection/SearchSection.styles.scss';
import './LandingPageSkeleton.styles.scss';

const LandingPageSkeleton: React.FC = () => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Calculate wrapper height based on scaled content to prevent extra white space
  useEffect(() => {
    if (!wrapperRef.current || !contentRef.current) return;

    const updateWrapperHeight = () => {
      requestAnimationFrame(() => {
        if (!contentRef.current || !wrapperRef.current) return;
        
        const contentHeight = contentRef.current.scrollHeight || 0;
        const scaledHeight = contentHeight * 0.65;
        wrapperRef.current.style.height = `${scaledHeight}px`;
      });
    };

    const timeoutId = setTimeout(updateWrapperHeight, 100);
    window.addEventListener('resize', updateWrapperHeight);
    
    const resizeObserver = new ResizeObserver(() => {
      updateWrapperHeight();
    });
    
    if (contentRef.current) {
      resizeObserver.observe(contentRef.current);
    }

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', updateWrapperHeight);
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <div className="landing-page-wrapper" ref={wrapperRef}>
      <div className="landing-page" ref={contentRef}>
      {/* Hero Section Skeleton */}
      <section className="hero-section">
        <div className="container">
          <div className="hero-content text-center">
            <Skeleton
              width="clamp(15rem, 30vw, 20rem)"
              height="4rem"
              className="hero-title-skeleton"
            />
            <Skeleton
              width="clamp(20rem, 70vw, 45rem)"
              height="1.5rem"
              className="hero-subtitle-skeleton"
            />
            <div className="search-section">
              <div className="search-container">
                <Skeleton width="100%" height="5rem" className="search-input-skeleton" />
              </div>
              <div className="search-suggestions">
                <Skeleton width="8rem" height="2.5rem" borderRadius="0.75rem" />
                <Skeleton width="10rem" height="2.5rem" borderRadius="0.75rem" />
                <Skeleton width="9rem" height="2.5rem" borderRadius="0.75rem" />
                <Skeleton width="7rem" height="2.5rem" borderRadius="0.75rem" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <main className="container">
        {/* Stats Section Skeleton */}
        <div className="stats-section-skeleton">
          <Skeleton width="100%" height="10rem" borderRadius="1rem" />
        </div>

        {/* Content Sections Skeleton */}
        {[1, 2, 3, 4, 5].map(sectionIndex => (
          <section key={sectionIndex} className="content-section">
            <div className="section-header">
              <div className="section-title">
                <Skeleton shape="circle" size="1.5rem" />
                <Skeleton width="12rem" height="1.5rem" />
                <Skeleton width="6rem" height="1.75rem" borderRadius="0.75rem" />
              </div>
              <div className="period-selector">
                <Skeleton width="8rem" height="2.5rem" borderRadius="0.75rem" />
              </div>
            </div>
            <div className="section-content">
              <Skeleton width="100%" height="25rem" borderRadius="0.75rem" />
            </div>
          </section>
        ))}
      </main>
      </div>
    </div>
  );
};

export default LandingPageSkeleton;
